class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.prevBtns = this.querySelectorAll('[data-slider-prev]');
    this.nextBtns = this.querySelectorAll('[data-slider-next]');
    this.dotsContainer = this.querySelector('[data-slider-dots]');

    this.current = 0;
    this.total = this.slides.length;
    this.autoplayDelay = parseInt(this.dataset.autoplay) || 3500;
    this.autoplayTimer = null;
    this.isTransitioning = false;

    this.tSlide = this.dataset.tSlide || 'Slide __N__';

    /* Respect prefers-reduced-motion (WCAG 2.2 AA — 2.3.3) */
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    /* Initial Accent-Color setzen (auch wenn nur 1 Slide existiert) */
    this.applyAccentColor(this.slides[0]?.dataset.color);

    if (this.total < 2) return;

    this.buildDots();
    this.bindEvents();
    this.goTo(0, false);

    if (!this.prefersReducedMotion.matches) {
      this.startAutoplay();
    }

    /* Listen for preference changes at runtime */
    this._onMotionPrefChange = () => {
      if (this.prefersReducedMotion.matches) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };
    this.prefersReducedMotion.addEventListener('change', this._onMotionPrefChange);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.prefersReducedMotion && this._onMotionPrefChange) {
      this.prefersReducedMotion.removeEventListener('change', this._onMotionPrefChange);
    }
  }

  buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.total; i++) {
      const dot = document.createElement('button');
      dot.className = 'hero__dot';
      dot.setAttribute('aria-label', this.tSlide.replace('__N__', i + 1));
      if (i === 0) dot.classList.add('hero__dot--active');
      dot.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(i);
      });
      this.dotsContainer.appendChild(dot);
    }
    this.dots = [...this.dotsContainer.querySelectorAll('.hero__dot')];
  }

  bindEvents() {
    /* Arrow-Click: advance + RESET autoplay-Timer (statt permanent stoppen).
       So bleibt das Slide-Wechsel-Pattern alle 7s aktiv auch nach manueller Interaktion. */
    this.prevBtns.forEach(btn => btn.addEventListener('click', () => {
      this.prev();
      this.resetAutoplay();
    }));
    this.nextBtns.forEach(btn => btn.addEventListener('click', () => {
      this.next();
      this.resetAutoplay();
    }));

    let touchStartX = 0;
    let touchEndX = 0;
    this.addEventListener('touchstart', e => { touchStartX = e.changedTouches[0].screenX; }, { passive: true });
    this.addEventListener('touchend', e => {
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? this.next() : this.prev();
        this.resetAutoplay();
      }
    }, { passive: true });
  }

  /* Restart autoplay-Timer (clear alten + neuen mit voller 7s-Wartezeit starten) */
  resetAutoplay() {
    this.stopAutoplay();
    if (this.autoplayDelay > 0 && !this.prefersReducedMotion.matches) {
      this.startAutoplay();
    }
  }

  prev() {
    this.goTo((this.current - 1 + this.total) % this.total);
  }

  next() {
    this.goTo((this.current + 1) % this.total);
  }

  goTo(index, animate = true) {
    if (this.isTransitioning && animate) return;
    this.current = index;

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('hero__slide--active', i === index);
      slide.setAttribute('aria-hidden', i !== index);
    });

    if (this.dots) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('hero__dot--active', i === index);
      });
    }

    /* Dynamische Accent-Color: setze --color-accent-active auf :root.
       Elements (Announcement-Bar etc.) lesen diese Variable und updaten automatisch. */
    this.applyAccentColor(this.slides[index]?.dataset.color);

    if (animate) {
      this.isTransitioning = true;
      setTimeout(() => { this.isTransitioning = false; }, 600);
    }
  }

  applyAccentColor(color) {
    if (!color) return;
    document.documentElement.style.setProperty('--color-accent-active', color);

    /* Map hex-color → scent name. Icons swap based on scent.
       Naming-Convention: icon-trust-anwendung.svg (default)
                        → icon-trust-anwendung-fruehlingsfrische.svg (pink)
                        → icon-trust-anwendung-duftfrei.svg (red)
                        → icon-trust-anwendung-ozeanfrische.svg (orange)
       Falls Variant 404, fällt zurück auf default (img.onerror). */
    const scentMap = {
      '#dc186a': 'fruehlingsfrische',
      '#d74a36': 'duftfrei',
      '#df7f1b': 'ozeanfrische'
    };
    const scent = scentMap[color.toLowerCase()] || null;
    document.documentElement.dataset.activeScent = scent || '';

    /* Swap aller Icons mit data-icon-base zur richtigen Color-Variant.
       Liest data-icon-{scent} direkt (Liquid-rendered URL mit korrektem ?v=Hash).
       Fallback auf data-icon-default wenn Scent-Variant nicht definiert. */
    document.querySelectorAll('[data-icon-base]').forEach((img) => {
      const defaultSrc = img.dataset.iconDefault;
      if (!defaultSrc) return;
      const variantSrc = (scent && img.dataset['icon' + scent.charAt(0).toUpperCase() + scent.slice(1)]) || defaultSrc;
      if (img.src !== variantSrc) img.src = variantSrc;
    });

    document.dispatchEvent(new CustomEvent('hero:accent-change', { detail: { color, scent } }));
  }

  startAutoplay() {
    if (this.autoplayDelay <= 0) return;
    this.autoplayTimer = setInterval(() => this.next(), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}
customElements.define('hero-slider', HeroSlider);
